import Vue from 'vue';
import Router, {Route, RouteConfig} from 'vue-router';
import AuthService from '@/modules/user/AuthService';
import EnvironmentService from '@/env/EnvironmentService';
import {RoleTypes} from '@/commons/enums/RoleTypes';
import DictionaryStore from '@/commons/services/store/DictionaryStore';
import ClientAuthService from '@/modules/client/services/ClientAuthService';
import ClientUserApi from '@/modules/client/services/ClientUserApi';
import CypressUtils from '@/env/cypress/CypressUtils';
import EnvironmentProducts from '@/env/services/EnvironmentProducts';
import {Routes} from './Routes';
import PartnerRoutes from '@/modules/partner/services/PartnerRoutes';
import LoanApplicationsRoutes from '@/modules/loanApplication/services/LoanApplicationsRoutes';
import RegulationRoutes from '@/modules/regulation/services/RegulationRoutes';
import AccreditationPanelRoutes from '@/modules/permissions/services/accreditationPanelRoutes';
import DealsRoutes from '@/modules/deals/services/DealsRoutes';
import ReportRoutes from '@/modules/report/services/ReportRoutes';
import FilemanagerRoutes from '@/modules/filemanager/FilemanagerRoutes';
import ProceduresRoutes from '@/modules/procedures/ProceduresRoutes';
import IssuesRoutes from '@/modules/issues/IssuesRoutes';
import DiscussionsRoutes from '@/modules/discussions/DiscussionsRoutes';
import ProspectsRoutes from '@/modules/prospects/services/ProspectsRoutes';
import {clone} from 'lodash-es';
import TrainingsRoutes from '@/modules/trainings/TrainingsRoutes';
import AdminUserPanelRoutes from '@/modules/adminPanel/services/AdminUserPanelRoutes';
import DelegationsListsRoutes from '@/modules/adminPanel/services/DelegationsListsRoutes';
import ClientRoutes from '@/modules/client/services/ClientRoutes';
import BhpRoutes from '@/modules/bhp/services/BhpRoutes';
import KnfRoutes from '@/modules/knf/services/KnfRoutes';
import WidgetRoutes from '@/modules/widgets/services/WidgetRoutes';
import LivespaceRoutes from '@/modules/livespace/services/LivespaceRoutes';
import AccreditationRoutes from '@/modules/accredition/services/AccreditationRoutes';
import {ProductType} from '@/commons/enums/ProductType';
import HumanResourcesRoutes from '@/modules/adminPanel/services/HumanResourcesRoutes';
// Navigation Wrapper musi zostać w main chunku z uwagi na post build
import NavigationWrapper from '@/modules/NavigationWrapper.vue';
import CalculatorRoutes from '@/components/calculator/CalculatorRoutes';
import {EmptyTemplateWithRouterView} from '@/router/templates';
import BranchesRoutes from '@/modules/branches/services/BranchRoutes';
import BankSecrecy from '@/modules/bankSecrecy/BankSecrecy.vue';
import AdminDivisionPanelRoutes from '@/modules/adminPanel/services/AdminDivisionPanelRoutes';
import UserApi from '@/modules/user/services/UserApi';
import RodoRoutes from '@/modules/rodo/services/RodoRoutes';
import CyberSecurityRoutes from '@/modules/cyberSecurity/services/CyberSecurityRoutes';

import AmlRoutes from '@/modules/aml/services/AmlRoutes';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {DashboardKey} from '@/modules/onboarding/tours/enums/DashboardKey';
import {PrintoutBeforeEnter} from '@/components/calculator/printout/PrintoutBeforeEnter';
import NPSRoutes from '@/modules/nps/services/NPSRoutes';
import {IUser} from '@/models/user/IUser';
import {Quizzes} from '@/models/user/Quizzes';
import DivisionRoutes from '@/modules/division/DivisionRoutes';
import {CrmModules} from '@/types/CrmModules';
import {ProvisionsRoutes} from '@/modules/provisions/ProvisionsRoutes';
import useModuleGuardService from '@/router/UseModuleGuardService';
import StatementRoutes from '@/modules/Statements/services/statementRoutes';
import DateUtils from '@/services/utils/DateUtils';
import HumanResourcesApi from '@/modules/adminPanel/services/HumanResourcesApi';
import {QuizCategory} from '@/models/Quiz';
import {QuizTestAttempt} from '@/modules/bhp/models/QuizTest';
import EthicQuizRoutes from '@/modules/Ethics/services/ethicRoutes';
import processProxy from '@/env/processProxy';

const MultiformEngineEditPreview = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEngineEditPreview.vue');
const MultiformEngineTest = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEngineTestPanel.vue');
const MultiformEnginePrint = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEnginePrint.vue');
const MultiformTestPanel = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/MultiformTestPanel.vue');
const Calculator = () => import(/* webpackChunkName: "Calculator" */ '@/components/calculator/Calculator.vue');

// const Livespace = () => import(/* webpackChunkName: "Calculator" */ '@/components/livespace/Livespace.vue');
const Dashboard = () => import(/* webpackChunkName: "Dashboard" */ '@/modules/dashboard/Dashboard.vue');
const ErrorPage = () => import(/* webpackChunkName: "ErrorPage" */ '@/components/ErrorPage.vue');
const NotFoundPage = () => import(/* webpackChunkName: "NotFoundPage" */ '@/components/NotFoundPage.vue');
const UserCalendar = () => import(/* webpackChunkName: "UserCalendar" */ '@/modules/calendar/UserCalendar.vue');
const Login = () => import(/* webpackChunkName: "Login" */ '@/modules/user/passwordManagement/Login.vue');
const Register = () => import(/* webpackChunkName: "Register" */ '@/modules/user/Register.vue');
const MeetTeam = () => import(/* webpackChunkName: "MeetTeam" */ '@/modules/meetTeam/view/MeetTeam.vue');
const UserEditPanel = () => import(/* webpackChunkName: "UserEditPanel" */ '@/modules/user/editUser/UserEditPanel.vue');
const LendiBegin = () => import(/* webpackChunkName: "LendiBegin" */ '@/modules/lendiBegin/LendiBegin.vue');
const BikReportSection = () => import(/* webpackChunkName: "BikReportSection" */ '@/modules/client/views/bik-report/BikReportSection.vue');
const PasswordChange = () => import(/* webpackChunkName: "PasswordChange" */ '@/modules/user/passwordManagement/PasswordChange.vue');
const PasswordReset = () => import(/* webpackChunkName: "PasswordReset" */ '@/modules/user/passwordManagement/PasswordReset.vue');
const MyCalendar = () => import(/* webpackChunkName: "MyCalendar" */ '@/modules/calendar/MyCalendar.vue');
const BlockedAccess = () => import(/* webpackChunkName: "BlockedAccess" */ '@/modules/user/blockedAccess/BlockedAccess.vue');
const ClientMultiform = () => import(/* webpackChunkName: "ClientMultiform" */ '@/modules/multiForm/views/ClientMultiform.vue');
const ClientLogin = () => import(/* webpackChunkName: "ClientLogin" */ '@/modules/client/views/ClientLogin.vue');
const ClientNavigation = () => import(/* webpackChunkName: "ClientNavigation" */ '@/modules/client/views/ClientNavigation.vue');
const LoanApplicationsCardList = () => import(/* webpackChunkName: "LoanApplicationsCardList" */ '@/modules/loanApplication/views/LoanApplicationsCardList.vue');
const ClientChecklist = () => import(/* webpackChunkName: "ClientChecklist" */ '../modules/client/views/client-checklist/ClientChecklist.vue');
const DealsForReporting = () => import(/* webpackChunkName: "DealsForReporting" */ '@/modules/deals/views/deals-for-reporting/DealsForReporting.vue');
const DiscountsComponent = () => import(/* webpackChunkName: "Discounts" */ '@/modules/discounts/DiscountsComponent.vue');
const BankBranchesPanel = () => import(/* webpackChunkName: "BankBranchesPanel" */ '@/modules/adminPanel/views/BankBranchesPanel.vue');
const FinancialInstitutionsList = () => import(/* webpackChunkName: "FinancialInstitutionsList" */ '@/modules/banks/FinancialInstitutionsList.vue');
const ProductsPanel = () => import(/* webpackChunkName: "ProductsPanel" */ '@/modules/products/ProductsPanel.vue');
const BigRaporstAgreements = () => import(/* webpackChunkName: "BigRaportsAgreements" */ '@/modules/adminPanel/views/BigRaportsAgreements.vue');
const SmsLogsPanel = () => import(/* webpackChunkName: "SmsLogsPanel" */ '@/modules/adminPanel/views/SmsLogsPanel.vue');
const ProspectSettings = () => import(/* webpackChunkName: "SmsLogsPanel" */ '@/modules/adminPanel/views/ProspectSettings.vue');
const HRLock = () => import(/* webpackChunkName: "HRLock" */ '@/modules/user/blockedAccess/HRLock.vue');
const EmailLogsPanel = () => import(/* webpackChunkName: "EmailLogsPanel" */ '@/modules/adminPanel/views/EmailLogsPanel.vue');
const OffersPrintout = () => import(/* webpackChunkName: "OffersPrintout" */ '@/modules/calculator/views/OffersPrintout.vue');
const SchedulePrintout = () => import(/* webpackChunkName: "SchedulePrintout" */ '@/modules/calculator/views/SchedulePrintout.vue');
const DesignPage = () => import(/* webpackChunkName: "DesignPage" */ '@/components/DesignPage.vue');
const TeleconferencePage = () => import(/* webpackChunkName: "MeetPage" */ '@/components/meet/TeleconferencePage.vue');
const MeetPage = () => import(/* webpackChunkName: "MeetPage" */ '@/components/meet/meetPage.vue');
const ProvisionPrint = () => import(/* webpackChunkName: "Provisions" */ '@/modules/provisions/views/print/ProvisionTablePrint.vue');
const CompanyAccountsView = () => import('@/modules/companyAccounts/CompanyAccountsView.vue');

export const TABLE_ID_KEY = 'tableUuid' as const;

Vue.use(Router);

const AppRoutes: RouteConfig[] = [
  {
    path: '/',
    redirect: Routes.DASHBOARD,
  },
  {
    name: Routes.DASHBOARD,
    path: Routes.DASHBOARD,
    component: Dashboard,
    meta: {
      title: () => I18NGetter().PlatformSection.dashboard,
      icon: 'mdi-view-dashboard-outline',
      mode: 'lendi',
      key: DashboardKey.DASHBOARD,
    },
  },
  {
    path: 'calendar',
    name: Routes.ACTIVITIES.CALENDAR,
    meta: {
      title: () => I18NGetter().PlatformSection.calendar,
      icon: 'mdi-calendar-outline',
      mode: 'crm',
      key: DashboardKey.CALENDAR,
      module: CrmModules.CALENDAR,
      condition: () => AuthService.User!.canAccessModule.CALENDAR && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
    component: MyCalendar,
  },
  {
    path: 'clients',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ClientRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.clients,
      icon: 'mdi-account-group-outline',
      mode: 'crm',
      key: DashboardKey.CLIENTS,
      condition: () => AuthService.User!.canAccessModule.CLIENTS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'deals',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DealsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.deals,
      icon: 'mdi-briefcase-outline',
      mode: 'crm',
      key: DashboardKey.DEALS,
      condition: () => AuthService.User!.canAccessModule.DEALS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'loan-application',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: LoanApplicationsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.loan_application,
      icon: 'mdi-file-document-outline',
      mode: 'crm',
      key: DashboardKey.LOAN_APPLICATIONS,
      module: CrmModules.LOAN_APPLICATIONS,
      condition: () => AuthService.User!.canAccessModule.LOAN_APPLICATIONS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'calendar/:user_id',
    name: Routes.ACTIVITIES.USER_CALENDAR,
    component: UserCalendar,
  },
  /*
  {
    path: Routes.STATISTIC,
    component: Statistics,
    meta: {
      title: 'Statystyki',
      icon: 'mdi-chart-line',
      mode: 'crm',
    },
  }, */
  {
    path: 'report',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ReportRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.report,
      icon: 'mdi-circle-multiple-outline',
      mode: 'crm',
      module: CrmModules.REPORTS,
      key: DashboardKey.SETTLEMENTS,
      condition: () =>
        AuthService.User!.canAccessModule.REPORTS &&
        AuthService.User!.privileges.hasAccessToSettlementView &&
        !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    name: Routes.DEALS.DEALS_FOR_REPORTING,
    path: 'deals-for-reporting',
    component: DealsForReporting,
  },
  {
    path: 'prospects',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ProspectsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.prospects,
      icon: 'mdi-sprout',
      mode: 'crm',
      module: CrmModules.PROSPECTS,
      condition: () => AuthService.User!.privileges.isAgentCC && EnvironmentService.Environment.isProspectsOn,
      cypressId: CypressUtils.ids.prospectsList,
    },
  },
  {
    path: 'partners',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: PartnerRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.partners,
      icon: 'mdi-handshake-outline',
      mode: 'crm',
      key: DashboardKey.PARTNERS,
      module: CrmModules.PARTNERS,
      condition: () => AuthService.User!.canAccessModule.PARTNERS,
      cypressId: CypressUtils.ids.partnersView,
    },
  },
  {
    path: 'branches',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: BranchesRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.branches,
      icon: 'mdi-domain',
      mode: 'admin',
      condition: () => AuthService.User!.isAdmin ||
        (AuthService.User!.isVerifier && AuthService.User!.privileges.canManageOrganisation),
    },
  },
  ...CalculatorRoutes,
  {
    path: 'calculator',
    meta: {
      title: () => I18NGetter().PlatformSection.calculator,
      icon: 'mdi-arrow-collapse',
      mode: 'lendi',
      condition: () => {
        const privileges = AuthService.User!.privileges;
        return AuthService.User?.isAppDomain.LENDI_PL &&
          (!privileges.mortgageLoansDisabled || !privileges.cashLoansDisabled || !privileges.smeLoansDisabled);
      },
    },
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    beforeEnter: (to, from, next) => {
      if (!AuthService.isLoggedIn) {
        next({name: Routes.PASSWORD_MANAGEMENT.LOGIN,});
      } else {
        const privileges = AuthService.User!.privileges;
        if (
          (to.name === ProductType.MORTGAGE && privileges.mortgageLoansDisabled) ||
          (to.name === ProductType.CASH && privileges.cashLoansDisabled) ||
          (to.name === ProductType.SME && privileges.smeLoansDisabled)
        ) {
          next({name: Routes.DASHBOARD,});
        } else {
          next();
        }
      }
    },
    children: EnvironmentProducts()
      .filter(x => x.simulationUrl)
      .map(loanType => ({
        path: `${loanType.type}/:simulationId?/:id?`,
        name: loanType.type,
        component: Calculator,
        props: {loanTypeUrl: loanType.simulationUrl,},
        meta: {
          title: loanType.nominative?.female,
          icon: loanType.icon,
          minNavigation: true,
          condition: () => AuthService.permitSimulation(loanType.type),
        },
      })),
  },
  {
    path: 'knowledge',
    meta: {
      title: () => I18NGetter().PlatformSection.knowledge,
      icon: 'mdi-library',
      mode: 'lendi',
      key: DashboardKey.KNOWLEDGE_BASE,
      module: CrmModules.KNOWLEDGE,
      condition: () => AuthService.User!.canAccessModule.KNOWLEDGE,
    },
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      ...RegulationRoutes,
      ...IssuesRoutes,
      ...TrainingsRoutes,
      ...FilemanagerRoutes,
      ...ProceduresRoutes,
    ],
  },
  {
    path: 'bik',
    component: BikReportSection,
    meta: {
      title: () => I18NGetter().PlatformSection.bik,
      icon: 'mdi-speedometer',
      mode: 'lendi',
      key: DashboardKey.BIK,
      module: CrmModules.BIK,
      condition: () => AuthService.User!.canAccessModule.BIK,
    },
  },
  {
    path: 'promotional-coupons',
    name: 'Discounts',
    component: DiscountsComponent,
    meta: {
      title: I18NGetter().PlatformSection.discounts,
      icon: 'mdi-ticket-percent-outline',
      mode: 'lendi',
      condition: () => AuthService.User!.canAccessModule.DISCOUNTS,
    },
  },
  {
    path: 'earn-with-lendi',
    name: 'CompanyAccounts',
    component: CompanyAccountsView,
    meta: {
      title: I18NGetter().PlatformSection.earn_with_lendi,
      icon: 'mdi-hand-coin-outline',
      mode: 'lendi',
      condition: () => AuthService.User!.canAccessModule.EARN_WITH_LENDI,
      badge: {
        is: true,
        text: 'new',
      },
    },
  },
  {
    path: 'lendi-begin',
    meta: {
      title: () => I18NGetter().PlatformSection.lendi_begin,
      icon: 'mdi-flag-checkered',
      module: CrmModules.LENDI_BEGININGS,
      mode: 'about',
      condition: () => AuthService.User!.canAccessModule.LENDI_BEGININGS,
    },
    component: LendiBegin,
  },
  {
    path: 'widgets',
    meta: {
      title: () => I18NGetter().PlatformSection.widgets,
      icon: 'mdi-widgets-outline',
      mode: 'about',
      minNavigation: true,
      module: CrmModules.WIDGETS,
    },
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: WidgetRoutes,
  },
  {
    path: 'divisions',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DivisionRoutes,
  },
  {
    path: 'livespace',
    meta: {
      title: 'Livespace',
      icon: 'mdi-desktop-classic',
      mode: 'admin',
      module: CrmModules.LIVESPACE,
      minNavigation: true,
      condition: () => {
        return AuthService.User!.isAdmin || AuthService.User!.isVerifier;
      } ,
    },
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: LivespaceRoutes,
  },
  {
    path: 'about',
    name: Routes.ABOUT,
    component: MeetTeam,
    meta: {
      title: () => I18NGetter().PlatformSection.about,
      icon: 'mdi-account-group',
      mode: 'about',
      key: DashboardKey.LENDI_TEAM,
      module: CrmModules.LENDI_CREW,
      condition: () => AuthService.User!.canAccessModule.LENDI_CREW,
    },
  },
  {
    path: 'accreditation_panel',
    name: 'AccreditationPanel',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AccreditationPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.accreditation_panel,
      icon: 'mdi-check-decagram-outline',
      mode: 'about',
      key: DashboardKey.ACCREDITATION_PANEL,
      module: CrmModules.ACCREDITATION_PANEL,
      condition: () => AuthService.User!.canAccessModule.ACCREDITATION_PANEL,
    },
  },
  {
    path: 'discussions',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DiscussionsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.discussions,
      icon: 'mdi-forum',
      mode: 'about',
      module: CrmModules.DISCUSSIONS,
      key: DashboardKey.FORUM,
      condition: () => AuthService.User!.canAccessModule.DISCUSSIONS &&
        !AuthService.User!.isLendiCareChiefWithoutAccessToLoans &&
        (!AuthService.User!.isLendiCareUser || (AuthService.User!.isLendiCareUser && AuthService.User!.hasAccessToLoans)),
    },
  },
  {
    path: 'verification',
    name: 'Verification',
    meta: {
      title: () => I18NGetter().PlatformSection.verification_panel,
      icon: 'mdi-folder-search-outline',
      mode: 'admin',
      condition: () => AuthService.isCurrentlyVerifier,
    },
  },
  {
    path: 'provisions-table',
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: ProvisionsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.provisions,
      icon: 'mdi-currency-usd',
      mode: 'admin',
      minNavigation: true,
      condition: () => AuthService.User?.privileges.canEditProvisions,
    },
  },
  {
    path: 'provisions',
    name: 'provisions',
    meta: {
      title: () => I18NGetter().PlatformSection.commissions,
      icon: 'mdi-currency-usd',
      mode: 'admin',
      condition: () => AuthService.User?.privileges.canEditProvisions,
      module: CrmModules.OLD_COMMISSIONS,
      action: () => {
        window.open(`${processProxy.VUE_APP_SIMULATION_URL}provisions`, '_blank');
      },
    },
  },
  {
    path: 'users-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AdminUserPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.users_managments,
      icon: 'mdi-account-tie-outline',
      mode: 'admin',
      module: CrmModules.USER_MANAGEMENT,
      condition: () => AuthService.User?.canAccessModule.USER_MANAGEMENT,
    },
  },
  {
    path: 'divisions-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AdminDivisionPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.divisions_management,
      icon: 'mdi-account-group-outline',
      mode: 'admin',
      condition: () => AuthService.User && (AuthService.User.isAdmin || AuthService.User.isVerifier),
    },
  },
  {
    path: 'nps-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: NPSRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.nps,
      icon: 'mdi-message-draw',
      mode: 'admin',
      module: CrmModules.NPS,
      condition: () => AuthService.User!.canAccessModule.NPS,
    },
  },
  {
    path: 'human-resources',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: HumanResourcesRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.human_resources,
      icon: 'mdi-list-status',
      mode: 'admin',
      module: CrmModules.HUMAN_RESOURCES,
      condition: () => AuthService.User!.canAccessModule.HUMAN_RESOURCES,
    },
  },
  {
    path: 'delegation-lists',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DelegationsListsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.delegations_lists,
      icon: 'mdi-format-list-bulleted',
      mode: 'admin',
      module: CrmModules.DELEGATION_LIST,
      condition: () => AuthService.User!.canAccessModule.DELEGATION_LIST,
    },
  },
  {
    path: 'management/bank/branches',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.BANK_BRANCHES_LIST,
        component: BankBranchesPanel,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.bank_branches,
      icon: 'mdi-bank-plus',
      mode: 'admin',
      module: CrmModules.BANK_BRANCHES,
      cypressId: CypressUtils.ids.bankBranchesView,
      condition: () => AuthService.User && (AuthService.User.isAdmin || (AuthService.User.isVerifier)),
    },
  },
  {
    path: 'management/products',
    name: Routes.ADMIN.PRODUCTS,
    component: ProductsPanel,
    meta: {
      title: () => I18NGetter().PlatformSection.products,
      icon: 'mdi-package-variant-closed',
      mode: 'admin',
      module: CrmModules.PRODUCTS,
      cypressId: CypressUtils.ids.financialInstitiutionsView,
      condition: () => AuthService.User?.isAdmin,
    },
  },
  {
    path: 'management/banks',
    name: Routes.ADMIN.FINANCIAL_INSTITUTION_LIST,
    component: FinancialInstitutionsList,
    meta: {
      title: () => I18NGetter().PlatformSection.banks,
      icon: 'mdi-bank',
      mode: 'admin',
      condition: () => AuthService.User?.isAdmin,
    },
  },
  {
    path: 'management/approvals/big',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.BIG_RAPORTS_AGREEMENTS,
        component: BigRaporstAgreements,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.approvals_bik,
      icon: 'mdi-clipboard-check',
      mode: 'admin',
      module: CrmModules.BIG_AGREEMENTS,
      cypressId: CypressUtils.ids.bigAgreements,
      condition: () => AuthService.User!.canAccessModule.BIG_AGREEMENTS,
    },
  },
  {
    path: 'management/sms/logs',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.SMS_LIST,
        component: SmsLogsPanel,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.sms_logs,
      icon: 'mdi-message-processing',
      mode: 'admin',
      cypressId: CypressUtils.ids.bankBranchesView,
      module: CrmModules.SMS_LOGS,
      condition: () => AuthService.User!.canAccessModule.SMS_LOGS,
    },
  },
  {
    path: 'management/email/logs',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.EMAIL_LIST,
        component: EmailLogsPanel,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.email_logs,
      icon: 'mdi-mail',
      mode: 'admin',
      module: CrmModules.EMAIL_LOGS,
      cypressId: CypressUtils.ids.bankBranchesView,
      condition: () => AuthService.User!.canAccessModule.EMAIL_LOGS,
    },
  },
  {
    path: 'multiform',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.MULTIFORM_TEST_PANEL,
        component: MultiformTestPanel,
      },
      {
        path: 'engine-test/:id',
        name: Routes.ADMIN.MULTIFORM_ENGINE_TEST,
        component: MultiformEngineTest,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.multiform,
      icon: 'mdi-file-document-multiple',
      mode: 'admin',
      condition: () => AuthService.User?.canEditModule.MULTIFORM,
    },
  },
  {
    path: 'management/prospect-settings',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.PROSPECT_SETTINGS,
        component: ProspectSettings,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.prospect_settings,
      icon: 'mdi-cog',
      mode: 'admin',
      module: CrmModules.PROSPECTS_SETTINGS,
      cypressId: CypressUtils.ids.prospectSettingsView,
      condition: () => AuthService.User!.canAccessModule.PROSPECTS_SETTINGS,
    },
  },
  /*
  {
    path: 'lendi-practices',
    name: Routes.LENDI_BEGIN.LENDI_PRACTICES,
    meta: {
      title: 'Dobre Praktyki',
      icon: 'mdi-rocket',
      mode: 'about',
    },
    component: LendiPractices,
  },
  */
  {
    path: '#',
    meta: {
      title: () => AuthService.isCurrentlyVerifier
        ? I18NGetter().useUserPanel.NEW_TRANSLATION_WORK_AS_ADVISOR
        : I18NGetter().useUserPanel.NEW_TRANSLATION_WORK_AS_VERIFIER ,
      mode: 'user',
      action: async() => {
        AuthService.User && AuthService.isCurrentlyVerifier
          ? await AuthService.dropRole()
          : await AuthService.changeRole(RoleTypes.VERIFIER);
      },
      condition: () =>
        AuthService.User !== null &&
        AuthService.User.isVerifier &&
        EnvironmentService.Environment.isVerificationOn,
    },
  },
  {
    name: Routes.USER_EDIT,
    path: 'user/edit',
    component: UserEditPanel,
    meta: {
      title: () => I18NGetter().useUserPanel.MY_DATA,
      mode: 'user',
      cypressId: CypressUtils.ids.myDetailsButton,
    },
  },
  /* {
    path: '#',
    meta: {
      title: 'Moje dokumenty',
      mode: 'user',
    },
  }, */
  {
    path: '/password-change',
    name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
    component: Login,
    meta: {
      title: () => I18NGetter().useUserPanel.CHANGE_PASSWORD,
      mode: 'user',
      action: () => {
        AppRouter.push({
          name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
        });
      },
    },
  },
];
const AppRouter = new Router({
  mode: 'history',
  base: processProxy.VUE_APP_CONTEXT_URL,
  scrollBehavior: (to, from, savedPosition) => {
    return to.name !== from.name ? { x: 0, y: 0, } : savedPosition;
  },
  routes: [
    {
      path: '/',
      redirect: `app/${Routes.DASHBOARD}`,
    },
    {
      path: '/bank-secrecy',
      name: Routes.BANK_SECRECY,
      component: BankSecrecy,
    },
    {
      path: '/login',
      name: Routes.PASSWORD_MANAGEMENT.LOGIN,
      component: Login,
    },
    {
      path: '/register',
      name: Routes.REGISTER,
      component: Register,
    },
    {
      path: '/password-change',
      name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
      component: PasswordChange,
      beforeEnter: (to, from, next) => {
        const isValid = AuthService.isTokenValid;
        if (isValid) {
          next();
        } else {
          AuthService.logout();
        }
      },
    },
    {
      path: '/password-reset',
      name: Routes.PASSWORD_MANAGEMENT.PASSWORD_RESET,
      component: PasswordReset,
    },
    {
      path: '/client',
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      children: [
        {
          path: 'login',
          name: Routes.CLIENT_USER.LOGIN,
          component: ClientLogin,
        },
        {
          path: '',
          name: Routes.CLIENT_USER.CLIENT_NAVIGATION,
          component: ClientNavigation,
          beforeEnter: async(to: Route, from, next) => {
            if (ClientAuthService.isTokenValid) {
              try {
                if (!ClientAuthService.User) {
                  await ClientAuthService.fetchUser();
                }
                await DictionaryStore.fetchAllDictionaries();
                if (to.name === Routes.CLIENT_USER.OFFERS) {
                  try {
                    if (!to.meta) {
                      to.meta = {};
                    }
                    to.meta.deal = await ClientUserApi.getDeal();
                    next();
                  } catch (e) {
                    next({
                      name: Routes.CLIENT_USER.MULTIFORM,
                      params: {
                        clientId: String(ClientAuthService.User?.id),
                      },
                    });
                  }
                } else {
                  next();
                }
              } catch (e) {
                await ClientAuthService.logout();
              }
            } else {
              await ClientAuthService.logout();
            }
          },
          children: [
            {
              path: 'multiform/:clientId?',
              name: Routes.CLIENT_USER.MULTIFORM,
              component: ClientMultiform,
            },
            {
              path: 'offers',
              name: Routes.CLIENT_USER.OFFERS,
              component: LoanApplicationsCardList,
            },
            {
              path: 'checklist',
              name: Routes.CLIENT_USER.CLIENT_CHECKLIST,
              component: ClientChecklist,
            },
            {
              path: 'documents',
              name: Routes.CLIENT_USER.DOCUMENTS,
            },
          ],
        },
      ],
    },
    {
      path: '/print',
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      children: [
        {
          path: `provisions/:productType/:${TABLE_ID_KEY}`,
          name: Routes.PROVISION.PRINT,
          component: ProvisionPrint,
        },
        {
          path: 'schedule/:productType/:simulationId/:offerId',
          name: Routes.NEW_CALCULATOR.SCHEDULE_PRINT,
          component: SchedulePrintout,
        },
        {
          path: ':productType/:simulationId',
          name: Routes.NEW_CALCULATOR.PRINT,
          component: OffersPrintout,
        },
      ],
      beforeEnter: PrintoutBeforeEnter,
    },
    {
      path: '/multiform/engine/:id',
      name: Routes.MULTIFORM_ENGINE_PRINT,
      component: MultiformEnginePrint,
    },
    {
      path: '/multiform/engine-test/:id',
      name: Routes.MULTIFORM_ENGINE_TEST,
      component: MultiformEngineTest,
    },
    {
      path: '/multiform/engine/preview/:id',
      name: Routes.MULTIFORM_ENGINE_PREVIEW,
      component: MultiformEngineEditPreview,
    },
    {
      path: '/design',
      name: Routes.DESIGN,
      component: DesignPage,
    },
    {
      path: '/error',
      name: Routes.ERROR,
      props: true,
      beforeEnter: async(to: Route, from, next) => {
        if (!to.query.redirect) {
          next({name: Routes.DASHBOARD,});
        } else {
          next();
        }
      },
      component: ErrorPage,
    },
    {
      path: '',
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      beforeEnter: async(to: Route, from, next) => {
        const queryToken = to.query.authToken;
        if (queryToken && typeof queryToken === 'string') {
          AuthService.token = queryToken;
          const newQuery = { ...to.query, };
          delete newQuery.authToken;
          await AppRouter.replace({
            path: to.path,
            query: newQuery,
            params: to.params,
          });
        }
        const isValid = AuthService.isTokenValid && !AuthService.isTokenExpired();
        const isValidUserData = isValid && AuthService.User;
        if (isValidUserData) {
          await DictionaryStore.fetchAllDictionaries();
        }
        if (isValidUserData && AuthService.User?.requirePasswordChange) {
          next({name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,},);
          Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_PASSWORD_CHANGE_REQUIRED);
        } else if (isValidUserData && AuthService.User?.forceLogout) {
          await AuthService.logout();
          Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_USER_LOGGED_OUT);
        } else if (isValid && !AuthService.User) {
          try {
            AuthService.User = await UserApi.userInfo();
            await DictionaryStore.fetchAllDictionaries();
            if (AuthService.User!.privileges.requireAccreditation) {
              const accsPaths = AccreditationRoutes.children?.map(x => x.name).filter(x => x !== undefined);
              if (from.name) {
                if (accsPaths?.includes(from.name)) {
                  next();
                }
              } else {
                next({name: Routes.ACCREDITATION_BASE,});
              }
            } else {
              next();
            }
          } catch (e) {
            console.error(e);
            Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_FAILED_TO_FETCH_USER_DATA);
            next({name: Routes.ERROR, query: { redirect: 'true', }, });
          }
        } else if (isValid) {
          next();
        } else if (AuthService.isTokenValid && AuthService.isTokenExpired()) {
          console.warn('Token invalid');
          await AuthService.logout(to);
        } else {
          next({name: Routes.PASSWORD_MANAGEMENT.LOGIN,});
        }
      },
      children: [
        {
          path: '/blocked-access',
          name: Routes.BLOCKED_ACCESS,
          component: BlockedAccess,
          beforeEnter: async(to, from, next) => {
            const user: IUser = AuthService.User!;
            if (user.privileges.isHrLocked) {
              next({name: Routes.HR_LOCKED,});
            } else {
              next();
            }
          },
        },
        {
          path: '/hr-locked',
          name: Routes.HR_LOCKED,
          component: HRLock,
          beforeEnter: async(to, from, next) => {
            const user: IUser = AuthService.User!;
            const quiz: Quizzes | undefined = user.quiz;
            if (user.privileges.requireAccreditation && user.privileges.isHrLocked) {
              next({name: Routes.ACCREDITATION.COMPANY_DATA,},);
            }  else if (quiz?.requireKnf) {
              next({name: Routes.KNF_TEST.START_KNF_TEST,},);
            } else if (quiz?.requireAml) {
              next({name: Routes.AML_TEST.START_AML_TEST,});
            } else if (quiz?.requireRodo) {
              next({name: Routes.RODO_TEST.START_RODO_TEST,});
            } else if (quiz?.requireCyber) {
              next({name: Routes.CYBER_SECURITY_TEST.START_CYBER_SECURITY_TEST,});
            } else if (quiz?.requireOsh) {
              next({name: Routes.BHP_TEST.START_BHP_TEST,},);
            } else if (quiz?.requireEthic) {
              next({name: Routes.ETHIC_QUIZZ.START_ETHIC_QUIZZ})
            } else if (quiz?.requireStmt) {
              next({name: Routes.STATEMENTS_TEST.START_STATEMENTS_TEST,})
            } else {
              next();
            }
          },
        },
        {
          path: '/app',
          name: 'NavigationWrapper',
          component: NavigationWrapper,
          beforeEnter: async(to, from, next) => {
            const user: IUser = AuthService.User!;
            const quiz: Quizzes | undefined = user.quiz;
            if (AuthService.isSystemBlocked) {
              next({name: Routes.BLOCKED_ACCESS,});
              console.warn('System access blocked');
            } else if (user.privileges.requireAccreditation) {
              next({name: Routes.ACCREDITATION.COMPANY_DATA,},);
            } else if (quiz?.requireKnf) {
              next({name: Routes.KNF_TEST.START_KNF_TEST,},);
            } else if (quiz?.requireAml) {
              next({name: Routes.AML_TEST.START_AML_TEST,});
            } else if (quiz?.requireRodo) {
              next({name: Routes.RODO_TEST.START_RODO_TEST,});
            } else if (quiz?.requireCyber) {
              next({name: Routes.CYBER_SECURITY_TEST.START_CYBER_SECURITY_TEST,});
            } else if (quiz?.requireOsh) {
              next({name: Routes.BHP_TEST.START_BHP_TEST,},);
            } else if (user.privileges.isHrLocked) {
              next({name: Routes.HR_LOCKED,},);
            } else {
              next();
            }
          },
          children: AppRoutes,
        },
        AccreditationRoutes,
        BhpRoutes,
        KnfRoutes,
        RodoRoutes,
        AmlRoutes,
        CyberSecurityRoutes,
        StatementRoutes,
        EthicQuizRoutes
      ],
    },
    {
      path: `/${Routes.TELECONFERENCE}/:uuid`,
      name: Routes.TELECONFERENCE,
      component: TeleconferencePage,
    },
    {
      path: `/${Routes.MEET}/:roomName`,
      name: Routes.MEET,
      component: MeetPage,
    },
    {
      path: '*',
      component: NotFoundPage,
    },
  ],
});

function patchRouterMethod(router: Router, methodName: 'push' | 'replace') {
  const old = clone(router);
  router[methodName] = async function(location: any) {
    return old[methodName](location).catch((error: any) => {
      if (['NavigationDuplicated', 'NavigationCancelled',].includes(error.name)) {
        return this.currentRoute;
      }
      if (error.message.includes('Redirected when going from') && error.message.includes('via a navigation guard')) {
        return this.currentRoute;
      }
      throw error;
    });
  };
}

function getRoutesPath(routesGroup: RouteConfig[], route: Route, routesPath: RouteConfig[] = []): RouteConfig[] {
  let currentRoutesPath: RouteConfig[] = [];
  const foundRoute = routesGroup.find(item => {
    if (item.name === route.name) {
      return true;
    }

    if (item.children?.length) {
      currentRoutesPath = getRoutesPath(item.children, route, routesPath);
      return !!currentRoutesPath.length;
    } else {
      return false;
    }
  });
  return foundRoute ? [ ...currentRoutesPath, foundRoute, ] : routesPath;
}

function checkRouteAccess(route: Route) {
  return !getRoutesPath(AppRoutes, route).some(item => typeof item.meta?.condition === 'function' && !item.meta?.condition());
}

patchRouterMethod(AppRouter, 'push');
patchRouterMethod(AppRouter, 'replace');

AppRouter.onError((error) => {
  console.warn('error', {error,});
  if (error.message.includes('chunk') && error.message.includes('failed')) {
    window.location.reload();
  }
});

AppRouter.beforeEach((to, from, next) => {
  if (EnvironmentService.Environment.isPreprodOrDev) {
    const {checkIfExcludedModule,} = useModuleGuardService();
    if (checkIfExcludedModule(to, AppRoutes)) {
      next({name: from.name ?? Routes.DASHBOARD,});
    } else next();
  } else next();
});

AppRouter.afterEach((to, from) => {
  if (!checkRouteAccess(to)) {
    AppRouter.push({name: Routes.DASHBOARD,}).then(() => {
      Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useMain.ERROR_NO_ACCESS);
    })
  }
});

export default AppRouter;
export {
  AppRoutes
};
